import React, { useEffect, useState } from "react";
import { api, kg, money, num, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import DateDMY from "@/components/DateDMY";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Download, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import SearchableSelect from "@/components/SearchableSelect";
import MultiSelect from "@/components/MultiSelect";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

const dash = (v) => v || "—";

const REPORTS = {
  transporter: {
    label: "Transporter Wise",
    endpoint: "/reports/transporter-wise",
    columns: [
      { k: "transporter_name", l: "Transporter" },
      { k: "date", l: "Date", fmt: fmtDate },
      { k: "vehicle_number", l: "Vehicle Number" },
      { k: "customer_name", l: "Customer Name" },
      { k: "product", l: "Product" },
      { k: "weight_kg", l: "Weight (Kg)", fmt: kg },
      { k: "approved_freight", l: "Approved Freight", fmt: money },
      { k: "transport_cost_per_kg", l: "Transport Cost/Kg", fmt: money },
    ],
  },
  customer: {
    label: "Customer Wise",
    endpoint: "/reports/customer-wise",
    columns: [
      { k: "date", l: "Date", fmt: fmtDate },
      { k: "customer_name", l: "Customer Name" },
      { k: "product", l: "Product" },
      { k: "brand", l: "Brand", fmt: dash, text: true },
      { k: "variant", l: "Variant", fmt: dash, text: true },
      { k: "packing", l: "Packing", fmt: dash, text: true },
      { k: "quantity", l: "Quantity Dispatched", fmt: (v) => num(v, 0) },
      { k: "approved_freight", l: "Approved Freight", fmt: money },
      { k: "transport_cost_per_kg", l: "Transport Cost/Kg", fmt: money },
    ],
  },
  product: {
    label: "Product & Variant Wise",
    endpoint: "/reports/product-variant-wise",
    columns: [
      { k: "product", l: "Product" },
      { k: "brand", l: "Brand" },
      { k: "variant", l: "Variant" },
      { k: "packing", l: "Packing Option" },
      { k: "quantity", l: "Gross Qty", fmt: (v) => num(v, 0) },
      { k: "weight_kg", l: "Gross Weight (Kg)", fmt: kg },
      { k: "returned_qty", l: "Returned Qty", fmt: (v) => num(v, 2) },
      { k: "returned_weight_kg", l: "Returned Wt (Kg)", fmt: kg },
      { k: "dm_qty", l: "D/M Qty", fmt: (v) => num(v, 2) },
      { k: "dm_weight_kg", l: "D/M Wt (Kg)", fmt: kg },
      { k: "net_qty", l: "Net Qty", fmt: (v) => num(v, 2) },
      { k: "net_weight_kg", l: "Net Wt (Kg)", fmt: kg },
    ],
  },
  returns: {
    label: "Returns",
    endpoint: "/reports/returns",
    columns: [
      { k: "date", l: "Dispatch Date", fmt: fmtDate },
      { k: "vehicle_number", l: "Vehicle" },
      { k: "transport_group", l: "Transport" },
      { k: "customer_name", l: "Customer" },
      { k: "invoice_number", l: "Invoice" },
      { k: "product", l: "Product" },
      { k: "packing", l: "Packing" },
      { k: "return_type", l: "Return Type" },
      { k: "last_return_date", l: "Last Return Date", fmt: (v) => (v ? fmtDate(v) : "-") },
      { k: "return_count", l: "# Returns", fmt: (v) => num(v, 0) },
      { k: "quantity", l: "Gross Qty", fmt: (v) => num(v, 0) },
      { k: "weight_kg", l: "Gross Wt (Kg)", fmt: kg },
      { k: "returned_qty", l: "Returned Qty", fmt: (v) => num(v, 2) },
      { k: "returned_weight_kg", l: "Returned Wt (Kg)", fmt: kg },
      { k: "net_qty", l: "Net Qty", fmt: (v) => num(v, 2) },
      { k: "net_weight_kg", l: "Net Wt (Kg)", fmt: kg },
      { k: "consignment_status", l: "Status" },
    ],
  },
  batch: {
    label: "Batch Wise",
    endpoint: "/reports/batch-wise",
    columns: [
      { k: "batch_number", l: "Batch #" },
      { k: "batch_quantity", l: "Batch Qty", fmt: (v) => (v === null || v === undefined || v === "") ? "-" : num(v, 2) },
      { k: "date", l: "Date", fmt: fmtDate },
      { k: "vehicle_number", l: "Vehicle" },
      { k: "customer_name", l: "Customer" },
      { k: "invoice_number", l: "Invoice" },
      { k: "product", l: "Product" },
      { k: "brand", l: "Brand" },
      { k: "variant", l: "Variant" },
      { k: "packing", l: "Packing" },
      { k: "quantity", l: "Line Qty", fmt: (v) => num(v, 0) },
      { k: "weight_kg", l: "Weight (Kg)", fmt: kg },
      { k: "pod", l: "POD" },
      { k: "delivery_date", l: "Delivery Date", fmt: (v) => (v ? fmtDate(v) : "-") },
    ],
  },
};

const SUMMARY_COLS = [
  { k: "transport_group", l: "Transport" },
  { k: "dispatches", l: "Dispatches", fmt: (v) => num(v, 0) },
  { k: "gross_qty", l: "Gross Qty", fmt: (v) => num(v, 2) },
  { k: "gross_weight_kg", l: "Gross Wt (Kg)", fmt: kg },
  { k: "returned_qty", l: "Returned Qty", fmt: (v) => num(v, 2) },
  { k: "returned_weight_kg", l: "Returned Wt (Kg)", fmt: kg },
  { k: "dm_qty", l: "D/M Qty", fmt: (v) => num(v, 2) },
  { k: "dm_weight_kg", l: "D/M Wt (Kg)", fmt: kg },
  { k: "net_qty", l: "Net Qty", fmt: (v) => num(v, 2) },
  { k: "net_weight_kg", l: "Net Wt (Kg)", fmt: kg },
  { k: "partial_returns", l: "Partial Returns", fmt: (v) => num(v, 0) },
  { k: "full_returns", l: "Full Returns", fmt: (v) => num(v, 0) },
];

function ReturnsSummary({ summary }) {
  return (
    <div className="table-wrap" data-testid="returns-summary">
      <div className="px-3 py-2 font-heading font-semibold text-slate-900">Gross vs Returned vs Net · Company vs Customer Transport</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
          <tr>{SUMMARY_COLS.map((c) => <th key={c.k} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{c.l}</th>)}</tr>
        </thead>
        <tbody>
          {summary.map((s, i) => (
            <tr key={s.transport_group} className={`border-b ${s.transport_group === "All" ? "bg-slate-900 text-white font-semibold" : ""}`} data-testid={`returns-summary-row-${i}`}>
              {SUMMARY_COLS.map((c) => (
                <td key={c.k} className="px-3 py-2 whitespace-nowrap font-mono-num">
                  {c.fmt ? c.fmt(s[c.k]) : s[c.k]}
                  {(c.k === "net_qty" || c.k === "net_weight_kg") && s.transport_group === "Customer Transport" && <div className="text-[10px] text-slate-400 font-sans">Net Retained</div>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Reports() {
  const [type, setType] = useState("transporter");
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const [transporters, setTransporters] = useState([]);
  const [transporterId, setTransporterId] = useState("");

  const [customers, setCustomers] = useState([]);
  const [customerSelection, setCustomerSelection] = useState("all"); // all | single | multi
  const [customerId, setCustomerId] = useState("");
  const [customerIds, setCustomerIds] = useState([]);

  const [batchQ, setBatchQ] = useState("");

  useEffect(() => {
    api.get("/transporters").then((r) => setTransporters(r.data.filter((t) => t.active)));
    api.get("/customers").then((r) => setCustomers(r.data.filter((c) => c.active)));
  }, []);

  const cfg = REPORTS[type];
  const selectedTransporter = transporters.find((t) => t.id === transporterId);
  const selectedCustomer = customers.find((c) => c.id === customerId);
  const selectedCustomers = customers.filter((c) => customerIds.includes(c.id));

  const customerSelectionLabel = () => {
    if (customerSelection === "all") return "All Customers";
    if (customerSelection === "single") return selectedCustomer?.name || "—";
    if (customerSelection === "multi") return selectedCustomers.length ? selectedCustomers.map((c) => c.name).join(", ") : "—";
    return "";
  };

  const generate = async () => {
    if (type === "transporter" && !transporterId) {
      toast.error("Select a transporter to generate this report");
      return;
    }
    if (type === "customer") {
      if (customerSelection === "single" && !customerId) return toast.error("Select a customer");
      if (customerSelection === "multi" && customerIds.length === 0) return toast.error("Select at least one customer");
    }
    setBusy(true);
    try {
      const params = { from_date: from, to_date: to };
      if (type === "transporter") params.transporter_id = transporterId;
      if (type === "customer") {
        if (customerSelection === "single" && customerId) params.customer_ids = customerId;
        if (customerSelection === "multi" && customerIds.length) params.customer_ids = customerIds.join(",");
      }
      if (type === "batch" && batchQ.trim()) params.batch_q = batchQ.trim();
      const r = await api.get(cfg.endpoint, { params });
      setSummary(null);
      if (type === "product") { setRows(r.data.rows); setTotals(r.data.totals); }
      else if (type === "returns") { setRows(r.data.rows); setTotals(null); setSummary(r.data.summary); }
      else { setRows(r.data); setTotals(null); }
    } catch (e) { toast.error("Failed to load report"); }
    finally { setBusy(false); }
  };

  const reportHeaderLines = () => {
    const lines = [`${cfg.label} Report`];
    if (type === "transporter") lines.push(`Transporter: ${selectedTransporter?.name || ""}`);
    if (type === "customer") lines.push(`Customer Selection: ${customerSelectionLabel()}`);
    if (type === "batch") lines.push(`Batch # Filter: ${batchQ.trim() || "All batches"}`);
    lines.push(`From Date: ${fmtDate(from)}`);
    lines.push(`To Date: ${fmtDate(to)}`);
    return lines;
  };

  const filename = () => {
    const base = `${type}-report-${from}-to-${to}`;
    if (type === "transporter" && selectedTransporter) {
      return `${base}-${selectedTransporter.name.replace(/[^a-z0-9]+/gi, "_")}`;
    }
    return base;
  };

  const summaryAoa = () => [
    SUMMARY_COLS.map((c) => c.l),
    ...summary.map((s) => SUMMARY_COLS.map((c) => (c.fmt ? c.fmt(s[c.k], s) : s[c.k]))),
  ];

  const exportExcel = () => {
    if (!rows) return;
    const header = cfg.columns.map((c) => c.l);
    const body = rows.map((r) => cfg.columns.map((c) => (c.fmt ? c.fmt(r[c.k]) : (r[c.k] ?? ""))));
    const meta = reportHeaderLines().map((line) => [line]);
    meta.push([]);
    const aoa = [...meta, header, ...body];
    if (summary) aoa.push([], ...summaryAoa());
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${filename()}.xlsx`);
  };

  const exportPDF = () => {
    if (!rows) return;
    const doc = new jsPDF({ orientation: "landscape" });
    const lines = reportHeaderLines();
    let y = 14;
    doc.setFontSize(14); doc.text(lines[0], 14, y); y += 6;
    doc.setFontSize(10);
    for (let i = 1; i < lines.length; i++) { doc.text(lines[i], 14, y); y += 5; }
    autoTable(doc, {
      startY: y + 2,
      head: [cfg.columns.map((c) => c.l)],
      body: rows.map((r) => cfg.columns.map((c) => (c.fmt ? c.fmt(r[c.k]) : (r[c.k] ?? "")))),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [37, 99, 235] },
    });
    if (summary) {
      const [h, ...b] = summaryAoa();
      autoTable(doc, { head: [h], body: b, styles: { fontSize: 8 }, headStyles: { fillColor: [15, 23, 42] } });
    }
    doc.save(`${filename()}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500 text-sm mt-1">Generate transporter, customer, or product reports for any date range.</p>
      </div>

      <Card>
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px]">
            <Label>Report Type</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setRows(null); setTotals(null); }}>
              <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REPORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type === "transporter" && (
            <div className="min-w-[240px]">
              <Label>Transporter *</Label>
              <SearchableSelect
                testid="report-transporter-select"
                value={transporterId}
                onChange={(v) => { setTransporterId(v); setRows(null); }}
                options={transporters.map((t) => ({ value: t.id, label: t.name }))}
                placeholder="Select transporter"
              />
            </div>
          )}
          {type === "batch" && (
            <div className="min-w-[220px]">
              <Label>Batch # (optional)</Label>
              <Input placeholder="e.g. BATCH-001" value={batchQ} onChange={(e) => { setBatchQ(e.target.value); setRows(null); }} data-testid="report-batch-filter" />
            </div>
          )}
          {type === "customer" && (
            <>
              <div className="min-w-[200px]">
                <Label>Customer Selection *</Label>
                <Select value={customerSelection} onValueChange={(v) => { setCustomerSelection(v); setCustomerId(""); setCustomerIds([]); setRows(null); }}>
                  <SelectTrigger data-testid="customer-selection-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    <SelectItem value="single">Single Customer</SelectItem>
                    <SelectItem value="multi">Multiple Customers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {customerSelection === "single" && (
                <div className="min-w-[240px]">
                  <Label>Customer *</Label>
                  <SearchableSelect
                    testid="report-customer-select"
                    value={customerId}
                    onChange={(v) => { setCustomerId(v); setRows(null); }}
                    options={customers.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Select customer"
                  />
                </div>
              )}
              {customerSelection === "multi" && (
                <div className="min-w-[280px]">
                  <Label>Customers *</Label>
                  <MultiSelect
                    testid="report-customer-multi"
                    values={customerIds}
                    onChange={(v) => { setCustomerIds(v); setRows(null); }}
                    options={customers.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Select customers"
                  />
                </div>
              )}
            </>
          )}
          <div><Label>From</Label><DateDMY value={from} onChange={setFrom} testid="report-from-date" /></div>
          <div><Label>To</Label><DateDMY value={to} onChange={setTo} minDate={from} testid="report-to-date" /></div>
          <div className="flex flex-col justify-end">
            <Label className="text-xs text-slate-500">Quick range</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="report-preset-chips">
              {(() => {
                const toIso = (d) => {
                  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
                  return `${y}-${m}-${dd}`;
                };
                const today = new Date();
                const startOfWeek = new Date(today); const dow = (today.getDay() + 6) % 7; startOfWeek.setDate(today.getDate() - dow); // Monday
                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const last30 = new Date(today); last30.setDate(today.getDate() - 29);
                const apply = (f, t) => { setFrom(toIso(f)); setTo(toIso(t)); setRows(null); };
                const chip = (label, onClick, tid) => (
                  <button key={label} type="button" onClick={onClick} data-testid={tid}
                    className="px-2.5 py-1 text-xs rounded-full border border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 text-slate-700 transition-colors">
                    {label}
                  </button>
                );
                return [
                  chip("Today", () => apply(today, today), "preset-today"),
                  chip("This Week", () => apply(startOfWeek, today), "preset-week"),
                  chip("This Month", () => apply(startOfMonth, today), "preset-month"),
                  chip("Last 30 Days", () => apply(last30, today), "preset-30"),
                ];
              })()}
            </div>
          </div>
          <Button onClick={generate} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="generate-report-btn">
            {busy ? "Loading…" : "Generate Report"}
          </Button>
          {rows && (
            <>
              <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn"><Download className="h-4 w-4 mr-1.5" /> Excel</Button>
              <Button variant="outline" onClick={exportPDF} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
            </>
          )}
        </CardContent>
      </Card>

      {rows && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm" data-testid="report-header-block">
            <div className="font-heading font-semibold text-base text-slate-900">{cfg.label} Report</div>
            {type === "transporter" && (
              <div className="text-slate-600 mt-1"><span className="font-medium">Transporter:</span> {selectedTransporter?.name || "—"}</div>
            )}
            {type === "customer" && (
              <div className="text-slate-600 mt-1"><span className="font-medium">Customer Selection:</span> {customerSelectionLabel()}</div>
            )}
            {type === "batch" && (
              <div className="text-slate-600 mt-1"><span className="font-medium">Batch # Filter:</span> {batchQ.trim() || "All batches"}</div>
            )}
            <div className="text-slate-600 flex flex-wrap gap-x-6">
              <div><span className="font-medium">From Date:</span> <span className="font-mono-num">{fmtDate(from)}</span></div>
              <div><span className="font-medium">To Date:</span> <span className="font-mono-num">{fmtDate(to)}</span></div>
            </div>
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
                <tr>{cfg.columns.map((c) => <th key={c.k} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{c.l}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50" data-testid={`report-row-${i}`}>
                    {cfg.columns.map((c) => (
                      <td key={c.k} className="px-3 py-2 whitespace-nowrap">
                        {c.fmt ? <span className={c.text ? "" : "font-mono-num"}>{c.fmt(r[c.k])}</span> : (r[c.k] || "-")}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={cfg.columns.length} className="text-center py-6 text-slate-500">No records in this range.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {summary && <ReturnsSummary summary={summary} />}

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="kpi-card" data-testid="report-returned-weight"><div className="text-xs text-slate-500">Total Returned</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.returned_weight)}</div></div>
          <div className="kpi-card" data-testid="report-dm-weight"><div className="text-xs text-slate-500">Damaged / Missing</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.dm_weight)}</div></div>
          <div className="kpi-card" data-testid="report-net-weight"><div className="text-xs text-slate-500">Net Weight</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.net_weight)}</div></div>
        </div>
      )}

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="kpi-card"><div className="text-xs text-slate-500">SMP · Atomizer</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.smp_atomizer_weight)}</div></div>
          <div className="kpi-card"><div className="text-xs text-slate-500">SMP · Nozzle</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.smp_nozzle_weight)}</div></div>
          <div className="kpi-card"><div className="text-xs text-slate-500">Desi Ghee</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.ghee_weight)}</div></div>
          <div className="kpi-card" data-testid="report-loose-ghee-weight"><div className="text-xs text-slate-500">Loose Ghee</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.loose_ghee_weight)}</div></div>
          <div className="kpi-card"><div className="text-xs text-slate-500">Overall Gross Total</div><div className="font-heading font-bold font-mono-num text-xl">{kg(totals.total_weight)}</div></div>
        </div>
      )}
    </div>
  );
}
